import { useEffect } from "react";
import { useUserInfoStore } from "@/stores/userInfoStore";
import useAuth from "hooks/useAuth";
import { usePushNotifications } from "@/hooks/usePushNotification";
import { useModal } from "@/hooks/useModal";
import { PushPermissionContent } from "./PushPermissionContent";
import { BlockedMessageContent } from "./BlockedMessageContent";
import { ModalType } from "@/models/common";
import {
  NOTIFICATION,
  PERMISSION_STATUS,
  PUSH_BLOCKED_MODAL_ID,
  PUSH_PERMISSION_MODAL_ID,
} from "@/constants/notification";

/**
 * PushNotificationManager Component
 *
 * Manages push notification permission flow for authenticated users.
 * Handles:
 * - Permission request popup
 * - Blocked/unsupported notification modal
 * - Klaviyo push subscription management
 *
 * This component should be placed at the layout level to work across all authenticated pages.
 */
export const PushNotificationManager: React.FC = () => {
  const auth: any = useAuth();
  const isLoggedIn: boolean = auth && auth.isAuthenticated;
  const loggedInUserData = useUserInfoStore((s) => s.userInfo);
  const { showModal, closeModal } = useModal();

  // Push notification hook
  const {
    shouldShowPermissionPopup,
    shouldShowBlockedPopup,
    handleAllow,
    handleReject,
    handleDismissForever,
    permissionStatus,
    retryCheckPermission,
  } = usePushNotifications(
    isLoggedIn,
    loggedInUserData?.accountId,
    loggedInUserData?.email,
  );

  // Show permission popup modal
  useEffect(() => {
    if (shouldShowPermissionPopup) {
      showModal({
        uId: PUSH_PERMISSION_MODAL_ID,
        closable: false,
        width: "450px",
        type: ModalType.CUSTOM,
        children: (
          <PushPermissionContent
            onAllow={async () => {
              await handleAllow();
              closeModal(PUSH_PERMISSION_MODAL_ID);
            }}
            onReject={() => {
              handleReject();
              closeModal(PUSH_PERMISSION_MODAL_ID);
            }}
            content={{
              title: NOTIFICATION.PERMISSION_INITIAL.PUSH_PERMISSION_TITLE,
              description:
                NOTIFICATION.PERMISSION_INITIAL.PUSH_PERMISSION_DESCRIPTION,
              allowButtonText:
                NOTIFICATION.PERMISSION_INITIAL
                  .PUSH_PERMISSION_ALLOW_BUTTON_TEXT,
              rejectButtonText:
                NOTIFICATION.PERMISSION_INITIAL
                  .PUSH_PERMISSION_REJECT_BUTTON_TEXT,
            }}
          />
        ),
      });
    }
  }, [
    shouldShowPermissionPopup,
    showModal,
    closeModal,
    handleAllow,
    handleReject,
  ]);

  // Show blocked message modal
  useEffect(() => {
    if (shouldShowBlockedPopup) {
      showModal({
        uId: PUSH_BLOCKED_MODAL_ID,
        closable: false,
        width: "450px",
        type: ModalType.CUSTOM,
        children: (
          <BlockedMessageContent
            onDismissForever={() => {
              handleDismissForever();
              closeModal(PUSH_BLOCKED_MODAL_ID);
            }}
            onRetry={async () => {
              await retryCheckPermission();
            }}
            permissionStatus={
              permissionStatus === PERMISSION_STATUS.UNSUPPORTED
                ? PERMISSION_STATUS.UNSUPPORTED
                : PERMISSION_STATUS.BLOCKED
            }
          />
        ),
      });
    }
  }, [
    shouldShowBlockedPopup,
    permissionStatus,
    showModal,
    closeModal,
    handleDismissForever,
    retryCheckPermission,
  ]);

  // This component doesn't render anything visible, it just manages modals
  return null;
};
